import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar,
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  FaTrophy, FaSyncAlt, FaBullhorn, FaMapMarkedAlt, FaUsers, FaBullseye, FaExclamationTriangle,
  FaHourglassHalf, FaArrowUp, FaArrowDown, FaMinus, FaHeartbeat, FaNewspaper, FaFire,
  FaClipboardCheck, FaLandmark, FaChartLine, FaDatabase, FaThumbsUp, FaThumbsDown, FaLightbulb, FaBalanceScale,
} from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, StatStrip, StatTile, StatLabel, StatValue, StatSub,
  TechBar, TechBarFill, TechChip, Grid, Span, Empty, Loading, ErrorBox, borderSoft, mono, accent, panelDeep,
} from '../../styles/techComponents';
import { getScoreboard } from '../../services/caminoService';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('es-AR'));
const C = { mine: '#38d39f', theirs: '#f87171', accent: '#00b4d8', warn: '#f59e0b', pos: '#22c55e', neg: '#ef4444', neu: '#94a3b8', purple: '#a855f7' };
const tip = { background: '#0b1320', border: `1px solid ${borderSoft}`, borderRadius: 8, fontSize: 12, color: '#e5e7eb', fontFamily: mono };
const tick = { fontSize: 11, fontFamily: mono, fill: '#94a3b8' };

const Panel = styled.div`
  border:1px solid ${borderSoft}; border-radius:10px; background:${panelDeep}; padding:0.85rem 0.95rem;
  h5{ margin:0 0 0.7rem; font-size:0.85rem; display:flex; align-items:center; gap:0.45rem; color:var(--text-primary); }
  h5 svg{ color:${accent}; }
`;
const Big = styled.div`
  display:flex; align-items:baseline; gap:0.5rem;
  .v{ font-size:2.6rem; font-weight:700; font-family:${mono}; line-height:1; color:var(--text-primary); }
  .u{ font-size:0.9rem; color:var(--text-secondary); }
`;
const MomTile = styled.div`
  border:1px solid ${borderSoft}; border-radius:9px; background:rgba(11,19,32,0.4); padding:0.7rem 0.8rem;
  .lab{ font-family:${mono}; font-size:0.66rem; letter-spacing:0.12em; text-transform:uppercase; color:var(--text-secondary); margin-bottom:0.35rem; }
  .val{ font-size:1.5rem; font-weight:700; font-family:${mono}; color:var(--text-primary); line-height:1; }
  .delta{ display:inline-flex; align-items:center; gap:0.25rem; font-family:${mono}; font-size:0.74rem; margin-top:0.4rem; }
`;
const Versus = styled.div` display:grid; gap:0.7rem; `;
const VRow = styled.div`
  border:1px solid ${borderSoft}; border-radius:8px; padding:0.75rem 0.85rem; background:rgba(11,19,32,0.4);
  .lab{ font-family:${mono}; font-size:0.68rem; letter-spacing:0.08em; text-transform:uppercase; color:var(--text-secondary); margin-bottom:0.55rem; }
  .row{ display:grid; grid-template-columns:82px 1fr 74px; gap:0.5rem; align-items:center; margin-bottom:6px; }
  .who{ font-size:0.82rem; } .n{ font-family:${mono}; font-variant-numeric:tabular-nums; text-align:right; }
`;
const Bar2 = styled.div` height:10px; border-radius:5px; background:rgba(255,255,255,0.06); overflow:hidden;
  > span{ display:block; height:100%; border-radius:5px; background:${({ $c }) => $c || accent}; width:${({ $w }) => $w}%; }
`;
const Tema = styled.span` display:inline-block; font-size:0.76rem; padding:0.25rem 0.55rem; border:1px solid ${borderSoft}; border-radius:999px;
  margin:0 0.35rem 0.35rem 0; color:var(--text-secondary); background:rgba(11,19,32,0.5); `;
const FunnelRow = styled.div`
  display:grid; grid-template-columns:120px 1fr 46px; gap:0.6rem; align-items:center; margin-bottom:8px;
  .name{ font-size:0.8rem; color:var(--text-secondary); text-transform:capitalize; }
  .n{ font-family:${mono}; text-align:right; color:var(--text-primary); }
`;

function VersusRow({ label, mine, theirs, mineLabel = 'Chouhy', theirLabel = 'Passaglia', suffix = '' }) {
  const max = Math.max(mine || 0, theirs || 0, 1);
  return (
    <VRow>
      <div className="lab">{label}</div>
      <div className="row">
        <span className="who">{mineLabel}</span>
        <Bar2 $c={C.mine} $w={Math.round(((mine || 0) / max) * 100)}><span /></Bar2>
        <span className="n" style={{ color: C.mine }}>{fmt(mine)}{suffix}</span>
      </div>
      <div className="row" style={{ marginBottom: 0 }}>
        <span className="who">{theirLabel}</span>
        <Bar2 $c={C.theirs} $w={Math.round(((theirs || 0) / max) * 100)}><span /></Bar2>
        <span className="n" style={{ color: C.theirs }}>{fmt(theirs)}{suffix}</span>
      </div>
    </VRow>
  );
}

function Delta({ d, pct, unit = '' }) {
  if (d == null) return <span className="delta" style={{ color: 'var(--text-disabled)' }}><FaMinus /> s/d</span>;
  const up = d > 0, flat = d === 0;
  const color = flat ? 'var(--text-secondary)' : up ? C.pos : C.neg;
  const Icon = flat ? FaMinus : up ? FaArrowUp : FaArrowDown;
  return (
    <span className="delta" style={{ color }}>
      <Icon /> {up ? '+' : ''}{fmt(d)}{unit} {pct != null && !flat ? `(${up ? '+' : ''}${pct}%)` : ''} <span style={{ color: 'var(--text-disabled)' }}>vs 30d prev</span>
    </span>
  );
}

const fmtDate = (d) => String(d).slice(5, 10);
const estadoLabel = { detectado: 'Detectados', tomado: 'Tomados', en_gestion: 'En gestión', resuelto: 'Resueltos' };
const funnelColor = { detectado: C.neu, tomado: C.accent, en_gestion: C.warn, resuelto: C.pos };

export default function Camino2027() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getScoreboard()); }
    catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <TechShell><Loading>Cargando tablero…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  const r = data.reconocimiento, t = data.territorio, c = data.contenido, o = data.objetivos, v = data.vs_passaglia;
  const rep = data.reputacion || {};
  const degradado = Array.isArray(data.degradado) ? data.degradado : [];
  const pilares = Array.isArray(data.pilares) ? data.pilares : [];
  const momentum = Array.isArray(data.momentum) ? data.momentum : [];
  const radarData = pilares.map((p) => ({ pilar: p.label, score: p.degradado ? 0 : (p.score || 0) }));
  const repSerie = (rep.serie || []).map((s) => ({ ...s, d: fmtDate(s.fecha) }));
  const semanas = (data.menciones_semanales || []).map((s) => ({ ...s, d: fmtDate(s.semana) }));
  const animo = [
    { name: 'A favor', value: rep.positivas || 0, color: C.pos },
    { name: 'En contra', value: rep.negativas || 0, color: C.neg },
    { name: 'Neutro', value: rep.neutras || 0, color: C.neu },
  ];
  const sentTotal = animo.reduce((a, x) => a + x.value, 0);
  const funnel = data.reclamos_funnel || [];
  const funnelMax = funnel.reduce((m, f) => Math.max(m, f.n), 0) || 1;
  const outlets = data.media_outlets_chouhy || [];
  const outletMax = outlets.reduce((m, x) => Math.max(m, x.n), 0) || 1;
  const emociones = (rep.emociones || []).slice(0, 6);
  const hcd = data.hcd_actividad;
  const fuentes = data.fuentes_datos || [];
  const argFavor = rep.argumentos_favor || [];
  const argContra = rep.argumentos_contra || [];
  const recos = rep.insight_persuasion || [];
  const hcdItems = hcd ? [
    { k: 'Ordenanzas', v: hcd.ordenanzas }, { k: 'Decretos', v: hcd.decretos },
    { k: 'Resoluciones', v: hcd.resoluciones }, { k: 'Comunicaciones', v: hcd.comunicaciones },
    { k: 'Expedientes', v: hcd.expedientes }, { k: 'Comisiones', v: hcd.comisiones },
  ].filter((x) => x.v != null) : [];

  return (
    <TechShell>
      <TechPageHeader
        title="Camino a la Intendencia 2027"
        dossier="NORTH-STAR"
        subtitle="Los indicadores clave de la candidatura, con datos reales de la plataforma, y la comparativa directa contra Passaglia/HECHOS."
        actions={<button onClick={load} style={{ background: 'none', border: `1px solid ${borderSoft}`, color: 'var(--text-secondary)', borderRadius: 6, padding: '0.4rem 0.7rem', cursor: 'pointer', fontFamily: mono, fontSize: '0.75rem' }}><FaSyncAlt /> Actualizar</button>}
      />

      {degradado.length > 0 && (
        <div style={{ border: '1px solid #f59e0b', borderRadius: 8, padding: '0.6rem 0.8rem', marginBottom: '0.9rem', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaExclamationTriangle /> No se pudieron cargar algunos indicadores ({degradado.join(', ')}). Esos bloques pueden mostrarse en 0 sin ser reales.
        </div>
      )}

      {/* Cuenta regresiva + índice de candidatura + radar */}
      <Grid style={{ marginBottom: '1.1rem' }}>
        <Span span={4} className="mobile-full-width">
          <Panel style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <div>
              <h5><FaHourglassHalf /> Cuenta regresiva a 2027</h5>
              <Big><span className="v" style={{ color: accent }}>{fmt(data.dias_para_eleccion)}</span><span className="u">días</span></Big>
              <StatSub style={{ marginTop: 6 }}>para las elecciones (est. {data.fecha_eleccion_estimada ? new Date(`${data.fecha_eleccion_estimada}T00:00`).toLocaleDateString('es-AR') : '—'})</StatSub>
            </div>
            <div style={{ borderTop: `1px solid ${borderSoft}`, paddingTop: '0.8rem' }}>
              <h5><FaTrophy /> Índice de candidatura</h5>
              <Big><span className="v" style={{ color: C.mine }}>{data.indice_candidatura ?? '—'}</span><span className="u">/100</span></Big>
              <TechBar style={{ marginTop: 8 }}><TechBarFill style={{ width: `${data.indice_candidatura || 0}%` }} /></TechBar>
              <StatSub style={{ marginTop: 6 }}>compuesto interno de los pilares reales · orientativo, no es una encuesta</StatSub>
            </div>
          </Panel>
        </Span>
        <Span span={8} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaChartLine /> Pilares de la candidatura (0-100)</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '0.6rem', alignItems: 'center' }} className="mobile-stack">
              <ResponsiveContainer width="100%" height={230}>
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="rgba(255,255,255,0.12)" />
                  <PolarAngleAxis dataKey="pilar" tick={{ fontSize: 10, fill: '#cbd5e1', fontFamily: mono }} />
                  <Radar dataKey="score" stroke={C.accent} fill={C.accent} fillOpacity={0.35} />
                  <Tooltip contentStyle={tip} />
                </RadarChart>
              </ResponsiveContainer>
              <div>
                {pilares.map((p) => (
                  <div key={p.key} style={{ display: 'grid', gridTemplateColumns: '108px 1fr 38px', gap: '0.5rem', alignItems: 'center', marginBottom: 7 }}>
                    <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{p.label}</span>
                    <Bar2 $c={p.degradado ? C.neu : C.accent} $w={p.degradado ? 0 : (p.score || 0)}><span /></Bar2>
                    <span style={{ fontFamily: mono, fontSize: '0.78rem', textAlign: 'right', color: p.degradado ? 'var(--text-disabled)' : 'var(--text-primary)' }}>
                      {p.degradado ? 's/d' : `${p.score ?? 0}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </Span>
      </Grid>

      {/* Momentum */}
      <TechSection title="Momentum · últimos 30 días vs período previo" icon={<FaFire />}>
        <StatStrip min="200px">
          {momentum.map((m, i) => (
            <MomTile key={i}>
              <div className="lab">{m.label}</div>
              <div className="val">{fmt(m.actual)}{m.unit || ''}</div>
              <Delta d={m.delta} pct={m.delta_pct} unit={m.unit || ''} />
            </MomTile>
          ))}
        </StatStrip>
      </TechSection>

      {/* KPIs generales */}
      <StatStrip style={{ marginTop: '1.1rem' }}>
        <StatTile><StatLabel><FaBullhorn /> Share of voice</StatLabel><StatValue>{r.share_of_voice_vs_passaglia_pct}%</StatValue><StatSub>menciones vs Passaglia (30d)</StatSub></StatTile>
        <StatTile><StatLabel><FaNewspaper /> Menciones</StatLabel><StatValue>{fmt(r.menciones_30d)}</StatValue><StatSub>en medios (30d)</StatSub></StatTile>
        <StatTile><StatLabel><FaHeartbeat /> Humor social</StatLabel><StatValue>{rep.score ?? '—'}</StatValue><StatSub>{rep.delta >= 0 ? <span style={{ color: C.pos }}>▲ +{rep.delta}</span> : <span style={{ color: C.neg }}>▼ {rep.delta}</span>} índice reputación</StatSub></StatTile>
        <StatTile><StatLabel><FaUsers /> Seguidores</StatLabel><StatValue>{fmt(r.seguidores_propios)}</StatValue><StatSub>cuentas propias</StatSub></StatTile>
        <StatTile><StatLabel><FaMapMarkedAlt /> Territorio</StatLabel><StatValue>{t.cobertura_pct}%</StatValue><StatSub>barrios activos (30d)</StatSub></StatTile>
        <StatTile><StatLabel><FaClipboardCheck /> Reclamos</StatLabel><StatValue>{t.resolution_rate_pct}%</StatValue><StatSub>tasa de resolución · {fmt(t.abiertos)} abiertos</StatSub></StatTile>
        <StatTile><StatLabel><FaLandmark /> Labor HCD</StatLabel><StatValue>{fmt(data.labor?.aprobados)}</StatValue><StatSub>aprobados · {fmt(data.labor?.en_tramite)} en trámite</StatSub></StatTile>
      </StatStrip>

      {/* Humor social en el tiempo + sentimiento + temas */}
      <Grid style={{ marginTop: '1.1rem' }}>
        <Span span={8} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaHeartbeat /> Humor social en el tiempo (índice 0-100)</h5>
            {repSerie.length ? (
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={repSerie} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <defs><linearGradient id="gcamScore" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.accent} stopOpacity={0.5} /><stop offset="100%" stopColor={C.accent} stopOpacity={0} />
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="d" tick={tick} /><YAxis domain={[0, 100]} tick={tick} />
                  <Tooltip contentStyle={tip} />
                  <Area type="monotone" dataKey="score" stroke={C.accent} strokeWidth={2} fill="url(#gcamScore)" name="Índice" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Empty>Sin serie de reputación aún.</Empty>}
          </Panel>
        </Span>
        <Span span={4} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaHeartbeat /> Ánimo actual</h5>
            {sentTotal > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={animo} dataKey="value" nameKey="name" innerRadius={42} outerRadius={72} paddingAngle={2}>
                    {animo.map((a, i) => <Cell key={i} fill={a.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Empty>Sin sentimiento clasificado.</Empty>}
            <StatSub style={{ textAlign: 'center' }}>{fmt(rep.comentarios_reales)} comentarios reales · fuente {rep.fuente || '—'}</StatSub>
          </Panel>
        </Span>
      </Grid>

      {(rep.temas?.length > 0 || rep.resumen) && (
        <TechSection title="Qué dice la conversación (comentarios reales, IA)" icon={<FaBullhorn />} style={{ marginTop: '1.1rem' }}>
          {rep.resumen && <p style={{ fontSize: '0.9rem', color: 'var(--text-primary)', lineHeight: 1.6, margin: '0 0 0.8rem' }}>{rep.resumen}</p>}
          {rep.temas?.length > 0 && (
            <div style={{ marginBottom: emociones.length ? '0.8rem' : 0 }}>
              {rep.temas.map((tm, i) => (
                <Tema key={i} style={{ borderColor: tm.lean === 'favor' ? 'rgba(34,197,94,0.4)' : tm.lean === 'contra' ? 'rgba(239,68,68,0.4)' : borderSoft }}>
                  {tm.tema} <span style={{ color: 'var(--text-disabled)', fontFamily: mono }}>· {tm.menciones}</span>
                </Tema>
              ))}
            </div>
          )}
          {emociones.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: '0.5rem', marginBottom: (argFavor.length || argContra.length || recos.length) ? '1rem' : 0 }}>
              {emociones.map((e, i) => (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 3, textTransform: 'capitalize' }}>
                    <span>{e.emocion}</span><span style={{ fontFamily: mono }}>{e.pct}%</span>
                  </div>
                  <Bar2 $c={C.purple} $w={Math.min(100, e.pct || 0)}><span /></Bar2>
                </div>
              ))}
            </div>
          )}
          {(argFavor.length > 0 || argContra.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }} className="mobile-stack">
              {argFavor.length > 0 && (
                <div style={{ border: `1px solid rgba(34,197,94,0.3)`, borderRadius: 8, padding: '0.7rem 0.8rem', background: 'rgba(34,197,94,0.05)' }}>
                  <div style={{ fontSize: '0.76rem', color: C.pos, fontFamily: mono, letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><FaThumbsUp /> LO QUE MOVILIZA EL APOYO</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.84rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                    {argFavor.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {argContra.length > 0 && (
                <div style={{ border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 8, padding: '0.7rem 0.8rem', background: 'rgba(239,68,68,0.05)' }}>
                  <div style={{ fontSize: '0.76rem', color: C.neg, fontFamily: mono, letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><FaThumbsDown /> LO QUE ALIMENTA LA CRÍTICA</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.84rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                    {argContra.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          {recos.length > 0 && (
            <div style={{ marginTop: '0.8rem', border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '0.7rem 0.8rem', background: 'rgba(0,180,216,0.05)' }}>
              <div style={{ fontSize: '0.76rem', color: accent, fontFamily: mono, letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><FaLightbulb /> RECOMENDACIONES DE COMUNICACIÓN (IA)</div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.84rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {recos.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </TechSection>
      )}

      {/* Menciones semanales Chouhy vs Passaglia */}
      <TechSection title="Menciones en medios por semana · Chouhy vs Passaglia" icon={<FaNewspaper />} style={{ marginTop: '1.1rem' }}>
        {semanas.length ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={semanas} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="d" tick={tick} /><YAxis tick={tick} allowDecimals={false} />
              <Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="chouhy" name="Chouhy" stroke={C.mine} strokeWidth={2.4} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="passaglia" name="Passaglia" stroke={C.theirs} strokeWidth={2.4} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty>Sin datos de menciones semanales.</Empty>}
      </TechSection>

      {/* Head-to-head + medios que cubren a Chouhy */}
      <Grid style={{ marginTop: '1.1rem' }}>
        <Span span={6} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaTrophy /> Chouhy vs Passaglia / HECHOS</h5>
            <Versus>
              <VersusRow label="Menciones en medios (30 días)" mine={v.menciones_chouhy_30d} theirs={v.menciones_passaglia_30d} />
              <VersusRow label="Share of voice" mine={v.sov_chouhy_pct} theirs={v.sov_passaglia_pct} suffix="%" />
              <VersusRow label="Seguidores en redes (monitoreadas)" mine={v.seguidores_chouhy} theirs={v.seguidores_passaglia} />
            </Versus>
          </Panel>
        </Span>
        <Span span={6} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaNewspaper /> Medios que más cubren a Chouhy (30d)</h5>
            {outlets.length ? outlets.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 140, flexShrink: 0, fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.outlet}</div>
                <div style={{ flex: 1, height: 9, background: 'rgba(255,255,255,0.05)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.round((s.n / outletMax) * 100)}%`, height: '100%', background: C.accent }} />
                </div>
                <div style={{ width: 28, textAlign: 'right', fontFamily: mono, fontSize: '0.78rem', color: 'var(--text-primary)' }}>{s.n}</div>
              </div>
            )) : <Empty>Sin cobertura registrada en 30 días.</Empty>}
          </Panel>
        </Span>
      </Grid>

      {/* Territorio: embudo de reclamos + gestión */}
      <Grid style={{ marginTop: '1.1rem' }}>
        <Span span={6} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaClipboardCheck /> Embudo de reclamos de vecinos</h5>
            {funnel.length ? funnel.map((f) => (
              <FunnelRow key={f.estado}>
                <span className="name">{estadoLabel[f.estado] || f.estado}</span>
                <Bar2 $c={funnelColor[f.estado] || C.accent} $w={Math.round((f.n / funnelMax) * 100)}><span /></Bar2>
                <span className="n">{fmt(f.n)}</span>
              </FunnelRow>
            )) : <Empty>Sin reclamos cargados.</Empty>}
            <StatSub style={{ marginTop: 8 }}>Tasa de resolución: <b style={{ color: C.mine }}>{t.resolution_rate_pct}%</b> · {fmt(t.resueltos_30d)} resueltos en 30d</StatSub>
          </Panel>
        </Span>
        <Span span={6} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaMapMarkedAlt /> Territorio y cobertura</h5>
            <StatStrip min="130px" style={{ marginBottom: '0.8rem' }}>
              <StatTile><StatLabel>Barrios activos</StatLabel><StatValue>{fmt(t.barrios_activos_30d)}<span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>/{fmt(t.barrios_total)}</span></StatValue><StatSub>últimos 30 días</StatSub></StatTile>
              <StatTile><StatLabel>Cobertura</StatLabel><StatValue>{t.cobertura_pct}%</StatValue><StatSub>de los barrios</StatSub></StatTile>
            </StatStrip>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontFamily: mono, marginBottom: 4 }}>COBERTURA TERRITORIAL</div>
            <TechBar><TechBarFill style={{ width: `${t.cobertura_pct}%` }} /></TechBar>
          </Panel>
        </Span>
      </Grid>

      {/* Contenido, Labor HCD, Objetivos */}
      <Grid style={{ marginTop: '1.1rem' }}>
        <Span span={4} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaBullhorn /> Contenido de campaña</h5>
            <StatStrip min="90px">
              <StatTile><StatLabel>Borradores</StatLabel><StatValue>{fmt(c.borradores)}</StatValue><StatSub>en producción</StatSub></StatTile>
              <StatTile><StatLabel>Pendientes</StatLabel><StatValue>{fmt(c.pendientes)}</StatValue><StatSub>a aprobar</StatSub></StatTile>
              <StatTile><StatLabel>Publicado 30d</StatLabel><StatValue>{fmt(c.publicado_30d)}</StatValue><StatSub>materiales</StatSub></StatTile>
            </StatStrip>
            <StatSub style={{ marginTop: 8 }}>{fmt(c.total)} materiales en total · flujo: borrador → pendiente → publicado</StatSub>
          </Panel>
        </Span>
        <Span span={4} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaLandmark /> Labor legislativa (HCD)</h5>
            <StatStrip min="90px">
              <StatTile><StatLabel>Aprobados</StatLabel><StatValue style={{ color: C.pos }}>{fmt(data.labor?.aprobados)}</StatValue><StatSub>proyectos</StatSub></StatTile>
              <StatTile><StatLabel>En trámite</StatLabel><StatValue style={{ color: C.warn }}>{fmt(data.labor?.en_tramite)}</StatValue><StatSub>presentados</StatSub></StatTile>
            </StatStrip>
            <StatSub style={{ marginTop: 8 }}>Tasa de aprobación: <b>{data.labor?.aprobacion_pct ?? 0}%</b></StatSub>
          </Panel>
        </Span>
        <Span span={4} className="mobile-full-width">
          <Panel style={{ height: '100%' }}>
            <h5><FaBullseye /> Objetivos del mandato (OKR)</h5>
            <Big><span className="v" style={{ fontSize: '1.9rem', color: C.mine }}>{o.avg_progress}%</span><span className="u">progreso</span></Big>
            <TechBar style={{ marginTop: 8 }}><TechBarFill style={{ width: `${o.avg_progress}%` }} /></TechBar>
            <StatSub style={{ marginTop: 8 }}>{o.total} objetivos · <span style={{ color: C.pos }}>{o.done} cumplidos</span> · <span style={{ color: C.warn }}>{o.at_risk} en riesgo</span></StatSub>
          </Panel>
        </Span>
      </Grid>

      {/* Actividad institucional del HCD (contexto) */}
      {hcdItems.length > 0 && (
        <TechSection title="Actividad institucional del Concejo (HCD) · contexto" icon={<FaLandmark />} style={{ marginTop: '1.1rem' }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 0.7rem', lineHeight: 1.5 }}>
            Volumen del cuerpo completo (todos los concejales, histórico), scrapeado e indexado del sitio del HCD. Es contexto institucional — <b>no</b> es la labor propia de Chouhy.
          </p>
          <StatStrip min="120px">
            {hcdItems.map((x, i) => (
              <StatTile key={i}><StatLabel>{x.k}</StatLabel><StatValue>{fmt(x.v)}</StatValue><StatSub>registros</StatSub></StatTile>
            ))}
          </StatStrip>
          {hcd?.fetched_at && (
            <StatSub style={{ marginTop: 8 }}>Fuente: hcd_snapshots · última captura {new Date(hcd.fetched_at).toLocaleDateString('es-AR')}</StatSub>
          )}
        </TechSection>
      )}

      {/* Trazabilidad de fuentes */}
      {fuentes.length > 0 && (
        <TechSection title="Fuentes de datos · trazabilidad" icon={<FaDatabase />} style={{ marginTop: '1.1rem' }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 0.8rem', lineHeight: 1.5 }}>
            Cada indicador de este tablero sale de datos reales de la plataforma. Acá está el origen, cómo se actualiza y cuántos registros tiene hoy — todo verificable.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-disabled)', fontFamily: mono, fontSize: '0.66rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  <th style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}` }}>Indicador</th>
                  <th style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}` }}>Origen</th>
                  <th style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}` }}>Cómo se actualiza</th>
                  <th style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}`, textAlign: 'right' }}>Registros</th>
                </tr>
              </thead>
              <tbody>
                {fuentes.map((f, i) => (
                  <tr key={i} style={{ color: 'var(--text-secondary)' }}>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}`, color: 'var(--text-primary)' }}>{f.label}</td>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}`, fontFamily: mono, color: accent }}>{f.tabla}</td>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}`, fontSize: '0.76rem' }}>{f.pipeline}</td>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${borderSoft}`, textAlign: 'right', fontFamily: mono, color: f.registros > 0 ? C.mine : 'var(--text-disabled)' }}>{fmt(f.registros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TechSection>
      )}

      <p style={{ fontFamily: mono, fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '1rem' }}>
        Datos reales de la plataforma · actualizado {new Date(data.updated_at).toLocaleString('es-AR')} · objetivo: <span style={{ color: accent }}>{data.objetivo}</span>
      </p>
    </TechShell>
  );
}
